/* eslint-disable @typescript-eslint/no-explicit-any */
import {Home, Logout, Person} from '@mui/icons-material';
import {AuthContext} from '../../providers/AuthProvider';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  ListItemIcon,
  Avatar,
  Divider,
  Menu,
  Tooltip,
} from '@mui/material';
import {fetchOpsTenants, getCurrentLoggedOps} from '../../providers/api/ops';
import {getTenant} from '../../providers/api/tenant';
import {app, setTenantKey} from '../../providers/Account';
import {updateOpsProfile} from '@digistaff/business';

interface Tenant {
  id: string;
  name: string;
  business: any;
  staff: any;
  logo: string;
}

export const AccountMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const {logOut} = useContext(AuthContext);
  const [logo, setLogo] = useState<null | string>(null);

  const open = Boolean(anchorEl);
  const isLocal = window.location.origin === process.env.LOCAL_ADMIN_URL;
  const opsId = useMemo(() => getCurrentLoggedOps(), []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccount = (newTab: boolean) => {
    const redirectBaseUrl = isLocal
      ? process.env.LOCAL_ACCOUNTS_URL
      : process.env.DS_ACCOUNTS_URL;

    const url = `${redirectBaseUrl}`;

    if (newTab) window.open(url, '_blank');
    else window.location.href = url;
  };

  const listTenants = useCallback(async (opsId: string): Promise<Tenant[]> => {
    console.time('start listTenant duration');

    const tenantIds = await fetchOpsTenants(opsId);

    const tenantProfiles = await Promise.all(
      tenantIds.map((id: any) => getTenant(id))
    );
    const tenantRows = tenantProfiles.map(profile => ({
      id: profile.id,
      name: profile.name,
      business: profile.businesses,
      staff: profile.staff,
      logo: profile.logo,
      status: profile.status ?? 'created',
    }));

    console.timeEnd('end listTenant duration');
    return tenantRows;
  }, []);

  const getLogo = useCallback(async () => {
    if (app.tenantKey) {
      try {
        const tenant = await getTenant(app.tenantKey);
        setLogo(tenant.logo);
      } catch (err) {
        console.error('Error fetching logo:', err);
      }
    }
  }, []);

  const switchTenant = useCallback(
    (id: string) => {
      updateOpsProfile(app, opsId, {
        current_selected_tenant: id,
      })
        .then(() => {
          setTenantKey(app, id);
          localStorage.setItem('tenant', id);
          location.reload();
        })
        .catch(err => console.error('Error switching tenant:', err));
    },
    [opsId]
  );

  // Initial load - fetch tenants and logo once
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [tenantsData] = await Promise.all([
          listTenants(opsId),
          getLogo(),
        ]);

        if (isMounted) {
          setTenants(tenantsData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [opsId, listTenants, getLogo]);

  const showTenantList = !loading && tenants.length > 1;

  return (
    <>
      <Box sx={{display: 'flex', alignItems: 'center', textAlign: 'center'}}>
        <Tooltip title="Account Settings">
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            {logo === null ? (
              <Person sx={{fontSize: '1.75em', color: 'white'}} />
            ) : (
              <Avatar src={logo} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            marginTop: '10px',
            overflow: 'auto',
            maxHeight: '90vh',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
      >
        <MenuItem
          onClick={() => handleAccount(false)}
          onAuxClick={() => handleAccount(true)}
        >
          <ListItemIcon>
            <Avatar />
          </ListItemIcon>
          My Account
        </MenuItem>
        <Divider />

        {loading && (
          <MenuItem disabled>
            <ListItemIcon>
              <Home fontSize="small" />
            </ListItemIcon>
            Loading Tenants...
          </MenuItem>
        )}

        {showTenantList &&
          tenants
            .filter((tenant: any) => tenant.status !== 'mark_as_deleted')
            .map((tenant: any) => (
              <MenuItem
                key={tenant.id}
                onClick={() => switchTenant(tenant.id)}
                sx={{
                  backgroundColor:
                    tenant.id === app.tenantKey ? 'lightgray' : 'white',
                }}
              >
                <ListItemIcon>
                  <Avatar src={tenant.logo}>
                    {tenant?.name?.substring(0, 1)}
                  </Avatar>
                </ListItemIcon>
                {tenant.name}
              </MenuItem>
            ))}
        {showTenantList && <Divider />}
        <MenuItem onClick={() => logOut()}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};
